// Tasks (WI12): members' and leads' tasks as the sheet kept them, one task with its log (who or what
// changed which field, and for an automated writer the chat and the line it read), the members'
// dashboard, and the Internal AIs tracker.
import { useState } from "react";

import { api, send } from "./api";
import { day, PACKS, when } from "./format";
import { useAct } from "./Members";
import { usePoll } from "./Pages";
import type { DashboardRow, Lists, MemberRow, Task, TaskLine, TrackerItem } from "./types";

export function Tasks({ onOpen }: { onOpen: (id: string) => void }) {
  const [kind, setKind] = useState("member");
  const [open, setOpen] = useState(true);
  const query = new URLSearchParams({ ...(kind ? { kind } : {}), open: String(open) }).toString();
  const { data, error, reload } = usePoll(() => api<Task[]>(`/api/tasks?${query}`), 30000);
  const [adding, setAdding] = useState(false);

  return (
    <div className="stack wide">
      <div className="actions">
        <select value={kind} onChange={(e) => (setKind(e.target.value), reload())}>
          <option value="member">Members' tasks</option>
          <option value="lead">Leads' tasks (free)</option>
          <option value="">Every task</option>
        </select>
        <label className="check">
          <input type="checkbox" checked={open} onChange={(e) => (setOpen(e.target.checked), reload())} />
          Open only
        </label>
        <button onClick={() => setAdding(!adding)}>{adding ? "Close" : "New task"}</button>
      </div>
      {adding && <NewTaskForm kind={kind || "member"} onDone={(id) => (setAdding(false), onOpen(id))} />}
      {error && <p className="note warn">{error}</p>}
      {!data ? (
        <p className="muted">Loading…</p>
      ) : data.length === 0 ? (
        <p className="muted">No tasks here.</p>
      ) : (
        <table className="list">
          <thead>
            <tr>
              <th>Task</th>
              <th>Member</th>
              <th>Category</th>
              <th>Status</th>
              <th>Target</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {data.map((t) => {
              const late = t.target_closure && !t.closed_at && new Date(t.target_closure) < new Date(new Date().toDateString());
              return (
                <tr key={t.id} onClick={() => onOpen(t.id)}>
                  <td>
                    {t.time_critical && <span className="badge warn">urgent</span>} {t.title}
                  </td>
                  <td>
                    {t.payer_name || t.household_name} {t.nid && <span className="muted">{t.nid}</span>}
                  </td>
                  <td>{t.category}</td>
                  <td>{t.status}</td>
                  <td className={late ? "warn" : ""}>{day(t.target_closure)}</td>
                  <td>{when(t.updated_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function NewTaskForm({ kind, onDone }: { kind: string; onDone: (id: string) => void }) {
  const { data: members } = usePoll(() => api<MemberRow[]>("/api/members"), 600000);
  const [household, setHousehold] = useState("");
  const [title, setTitle] = useState("");
  const { act, problem } = useAct(() => undefined);
  return (
    <div className="panel">
      <div className="grid2">
        <label>
          For
          <select value={household} onChange={(e) => setHousehold(e.target.value)}>
            <option value="">Choose a member or lead…</option>
            {(members ?? []).map((m) => (
              <option key={m.household_id} value={m.household_id}>
                {m.payer_name || m.household_name} ({m.status})
              </option>
            ))}
          </select>
        </label>
        <label>
          Task
          <input value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
      </div>
      {problem && <p className="note warn">{problem}</p>}
      <button
        disabled={!household || !title.trim()}
        onClick={async () => {
          const made = await act(() => send<{ id: string }>("/api/tasks", { household_id: household, kind, title }));
          if (made?.id) {
            onDone(made.id);
          }
        }}
      >
        Add
      </button>
    </div>
  );
}

const TEXT_FIELDS: [keyof Task, string][] = [
  ["vendor", "Vendor involved"],
  ["context", "Context"],
  ["next_step", "Next step"],
  ["sales_hooks", "Sales hooks (leads)"],
  ["learnings", "Learnings"],
];

export function TaskPage({ id, onMember }: { id: string; onMember: (household: string) => void }) {
  const { data, error, reload } = usePoll(() => api<{ task: Task; log: TaskLine[] }>(`/api/tasks/${id}`), 30000);
  const { data: lists } = usePoll(() => api<Lists>("/api/lists"), 600000);
  const [changes, setChanges] = useState<Record<string, unknown>>({});
  const [note, setNote] = useState("");
  const { act, problem } = useAct(reload);
  if (error) {
    return <p className="note warn">{error}</p>;
  }
  if (!data) {
    return <p className="muted">Loading…</p>;
  }
  const t = data.task;
  const value = (k: keyof Task) => (k in changes ? changes[k] : t[k]);
  const text = (k: keyof Task) => String(value(k) ?? "");
  const set = (k: keyof Task, v: unknown) => setChanges({ ...changes, [k]: v });
  const choices = (name: keyof Lists, current: string) => {
    const options = lists?.[name] ?? [];
    return current && !options.includes(current) ? [current, ...options] : options;
  };
  const save = async () => {
    const body = Object.fromEntries(Object.entries(changes).map(([k, v]) => [k, v === "" && k.endsWith("_closure") ? null : v]));
    if ((await act(() => send(`/api/tasks/${id}`, body, "PUT"))) !== null) {
      setChanges({});
    }
  };
  return (
    <div className="household">
      <div className="case-head">
        <h2>{t.title}</h2>
        <p>
          <button className="link" onClick={() => onMember(t.household_id)}>
            {t.household_name}
          </button>{" "}
          · {t.kind === "lead" ? "lead's task" : "member's task"} · made {when(t.created_at)}
          {t.source === "import" ? " · from the sheet" : ""}
          {t.closed_at ? ` · closed ${when(t.closed_at)}` : ""}
        </p>
      </div>
      {problem && <p className="note warn">{problem}</p>}
      <div className="columns">
        <section className="panel">
          <div className="grid2">
            <label>
              Task
              <input value={text("title")} onChange={(e) => set("title", e.target.value)} />
            </label>
            <label>
              Status
              <select value={text("status")} onChange={(e) => set("status", e.target.value)}>
                {choices("statuses", t.status).map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <label>
              Category
              <select value={text("category")} onChange={(e) => set("category", e.target.value)}>
                <option value="" />
                {choices("categories", t.category).map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <label>
              Initiator
              <select value={text("initiator")} onChange={(e) => set("initiator", e.target.value)}>
                <option value="" />
                {choices("initiators", t.initiator).map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <label>
              Target closure
              <input type="date" value={text("target_closure")} onChange={(e) => set("target_closure", e.target.value || null)} />
            </label>
            <label>
              Scheduled for (a later task)
              <input type="date" value={text("scheduled_for")} onChange={(e) => set("scheduled_for", e.target.value || null)} />
            </label>
          </div>
          <label className="check">
            <input type="checkbox" checked={Boolean(value("time_critical"))} onChange={(e) => set("time_critical", e.target.checked)} />
            Time critical
          </label>
          {TEXT_FIELDS.map(([k, label]) => (
            <label key={k}>
              {label}
              <textarea rows={k === "context" ? 4 : 2} value={text(k)} onChange={(e) => set(k, e.target.value)} />
            </label>
          ))}
          <div className="actions">
            <button disabled={Object.keys(changes).length === 0} onClick={save}>
              Save
            </button>
            {Object.keys(changes).length > 0 && (
              <button className="secondary" onClick={() => setChanges({})}>
                Undo
              </button>
            )}
          </div>
        </section>
        <section className="panel">
          <h3>Log</h3>
          <div className="actions">
            <input className="grow" placeholder="Add a note" value={note} onChange={(e) => setNote(e.target.value)} />
            <button
              disabled={!note.trim()}
              onClick={async () => (await act(() => send(`/api/tasks/${id}/notes`, { text: note }))) !== null && setNote("")}
            >
              Note
            </button>
          </div>
          {[...data.log].reverse().map((l, i) => (
            <div key={i} className="log-line">
              <div className="muted">
                {when(l.at)} · {l.actor}
                {l.shift ? ` · ${l.shift} shift` : ""}
                {l.source ? ` · ${l.source}` : ""}
              </div>
              {l.action === "note" ? (
                <div>{l.new_value}</div>
              ) : l.action === "create" ? (
                <div>Made: {l.new_value}</div>
              ) : (
                <div>
                  {l.field.replace(/_/g, " ")}: {l.old_value || "(empty)"} → {l.new_value || "(empty)"}
                </div>
              )}
              {l.evidence && <div className="muted">“{l.evidence}”</div>}
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}

export function Dashboard({ onOpen }: { onOpen: (household: string) => void }) {
  const { data, error } = usePoll(() => api<DashboardRow[]>("/api/dashboard"), 60000);
  if (error) {
    return <p className="note warn">{error}</p>;
  }
  if (!data) {
    return <p className="muted">Loading…</p>;
  }
  if (data.length === 0) {
    return <p className="muted">No members yet.</p>;
  }
  const yes = (b: boolean) => (b ? "Y" : "N");
  return (
    <table className="list">
      <thead>
        <tr>
          <th>Member</th>
          <th>Pack</th>
          <th>Family group</th>
          <th>Onboarding form</th>
          <th>Assure</th>
          <th>Initiated</th>
          <th>Completed</th>
          <th>Lapsed</th>
          <th>In progress</th>
          <th>Scheduled</th>
          <th>Reactivate?</th>
        </tr>
      </thead>
      <tbody>
        {data.map((r) => (
          <tr key={r.household_id} onClick={() => onOpen(r.household_id)}>
            <td>
              {r.name} {r.status === "paused" && <span className="muted">(paused)</span>}
            </td>
            <td>{r.pack ? PACKS[r.pack] : ""}</td>
            <td>{yes(r.family_group)}</td>
            <td>{yes(r.onboarding_form)}</td>
            <td>{r.assure === "none" ? "" : r.assure}</td>
            <td>{r.initiated}</td>
            <td>{r.completed}</td>
            <td className={r.lapsed > 0 ? "warn" : ""}>{r.lapsed}</td>
            <td>{r.in_progress}</td>
            <td>{r.scheduled}</td>
            <td>{r.reactivation ? <span className="badge warn">Y</span> : "N"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Tracker() {
  const { data, error, reload } = usePoll(() => api<TrackerItem[]>("/api/tracker"), 60000);
  const { act, problem } = useAct(reload);
  const [editing, setEditing] = useState<Partial<TrackerItem> | null>(null);
  const fields: [keyof TrackerItem, string][] = [
    ["name", "AI"],
    ["owner", "Owner"],
    ["status", "Status"],
    ["timeline", "Timeline"],
    ["notes", "Notes"],
  ];
  const save = async () => {
    if (!editing) {
      return;
    }
    const { id, ...body } = editing;
    if ((await act(() => (id ? send(`/api/tracker/${id}`, body, "PUT") : send("/api/tracker", body)))) !== null) {
      setEditing(null);
    }
  };
  return (
    <div className="stack wide">
      <p className="muted">Niro's own AI projects (the sheet's Internal AIs tab).</p>
      {error && <p className="note warn">{error}</p>}
      {problem && <p className="note warn">{problem}</p>}
      {data && data.length > 0 && (
        <table className="list">
          <thead>
            <tr>
              {fields.map(([k, label]) => (
                <th key={k}>{label}</th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.id} onClick={() => setEditing(item)}>
                {fields.map(([k]) => (
                  <td key={k}>{item[k]}</td>
                ))}
                <td>
                  <button
                    className="link"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Delete ${item.name}?`)) {
                        act(() => send(`/api/tracker/${item.id}`, {}, "DELETE"));
                      }
                    }}
                  >
                    delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing ? (
        <div className="panel">
          <div className="grid2">
            {fields.map(([k, label]) => (
              <label key={k}>
                {label}
                <input value={String(editing[k] ?? "")} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} />
              </label>
            ))}
          </div>
          <div className="actions">
            <button disabled={!String(editing.name ?? "").trim()} onClick={save}>
              Save
            </button>
            <button className="secondary" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div>
          <button onClick={() => setEditing({})}>Add an AI</button>
        </div>
      )}
    </div>
  );
}
